import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { memberPetitionService } from '../services/memberPetitions';
import { documentService } from '../services/documents';
import {
  MemberPetition,
  PetitionType,
  MemberPetitionStatus,
  CreatePetitionRequest,
} from '../types/petition';
import { EntityType } from '../types/changeRequest';
import { DocumentType } from '../types/document';
import { Container, Stack, Flex } from '../design-system/components/Layout';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text, Label } from '../design-system/components/Typography';
import { PageHeader } from '../design-system/patterns/PageHeader';
import Modal from '../components/common/Modal';
import EmptyState from '../components/common/EmptyState';
import { SkeletonCard } from '../components/common/Skeleton';
import {
  PetitionCreateForm,
  PetitionCreateFormValues,
} from '../components/petitions/PetitionCreateForm';
import { formatAthensDate, formatAthensDateTime } from '../utils/athensTime';
import { normalizePetitionStatus, normalizePetitionType } from '../utils/petition';
import { getApiErrorMessage } from '../utils/apiErrors';
import { formatPetitionPayload } from '../utils/petitionPayload';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { borders } from '../design-system/tokens/borders';
import { breakpoints } from '../design-system/tokens/breakpoints';

const PetitionsContainer = styled(Container)`
  max-width: 920px;
`;

const HelpCard = styled(Card)`
  padding: ${spacing[6]};
`;

const HelpSteps = styled.ol`
  margin: ${spacing[4]} 0 0 0;
  padding-left: ${spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};

  li {
    color: ${props => props.theme.colors.textSecondary};
    font-size: ${typography.fontSize.sm};
    font-family: ${typography.fontFamily.primary};
    line-height: ${typography.lineHeight.relaxed};
  }
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${spacing[3]};

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const SummaryPill = styled.button<{ $active: boolean; $tone: 'pending' | 'approved' | 'rejected' }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing[1]};
  padding: ${spacing[4]} ${spacing[5]};
  border-radius: ${borders.radius.lg};
  border: ${borders.width.thin} solid
    ${props => {
      if (!props.$active) return props.theme.colors.border;
      if (props.$tone === 'approved') return props.theme.colors.success;
      if (props.$tone === 'rejected') return props.theme.colors.error;
      return props.theme.colors.warning;
    }};
  background: ${props => {
    if (!props.$active) return props.theme.colors.surface;
    if (props.$tone === 'approved') return `${props.theme.colors.success}12`;
    if (props.$tone === 'rejected') return `${props.theme.colors.error}12`;
    return `${props.theme.colors.warning}14`;
  }};
  border-left-width: 3px;
  border-left-color: ${props => {
    if (props.$tone === 'approved') return props.theme.colors.success;
    if (props.$tone === 'rejected') return props.theme.colors.error;
    return props.theme.colors.warning;
  }};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    background: ${props => props.theme.colors.surfaceHover};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const SummaryCount = styled.span`
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.display};
  color: ${props => props.theme.colors.text};
  line-height: 1.1;
`;

const SummaryLabel = styled.span`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};
  font-family: ${typography.fontFamily.primary};
`;

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[4]};
`;

const FilterSelect = styled.select`
  padding: ${spacing[2]} ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const PetitionCardButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;

  &:focus-visible {
    outline: none;

    > div {
      outline: 2px solid ${props => props.theme.colors.primary};
      outline-offset: 2px;
    }
  }
`;

const PetitionCard = styled(Card)`
  padding: ${spacing[5]} ${spacing[6]};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.surfaceHover};
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[3]};

  @media (max-width: ${breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CardMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  min-width: 0;
`;

const PayloadPreview = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: ${typography.lineHeight.relaxed};
`;

const StatusBadge = styled.span<{ $tone: 'pending' | 'approved' | 'rejected' }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  flex-shrink: 0;
  padding: ${spacing[1]} ${spacing[3]};
  border-radius: ${borders.radius.full};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.primary};
  line-height: 1.3;
  white-space: nowrap;
  background: ${props => {
    if (props.$tone === 'approved') return props.theme.colors.successBg ?? `${props.theme.colors.success}18`;
    if (props.$tone === 'rejected') return props.theme.colors.errorBg ?? `${props.theme.colors.error}18`;
    return props.theme.colors.warningBg ?? `${props.theme.colors.warning}18`;
  }};
  color: ${props => {
    if (props.$tone === 'approved') return props.theme.colors.success;
    if (props.$tone === 'rejected') return props.theme.colors.error;
    return props.theme.colors.warning;
  }};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  gap: ${spacing[5]};
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const DetailLabel = styled.span`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};
  font-family: ${typography.fontFamily.primary};
`;

const DetailText = styled.p`
  margin: 0;
  font-size: ${typography.fontSize.base};
  line-height: ${typography.lineHeight.relaxed};
  color: ${props => props.theme.colors.text};
  font-family: ${typography.fontFamily.primary};
`;

const RejectionBlock = styled.div`
  padding: ${spacing[4]};
  background: ${props => props.theme.colors.error}12;
  border-left: 3px solid ${props => props.theme.colors.error};
  border-radius: ${borders.radius.sm};
`;

const ErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  padding: ${spacing[8]};
  text-align: center;
  gap: ${spacing[4]};
`;

const SkeletonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const ViewHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.primary};
  font-family: ${typography.fontFamily.primary};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const EMPTY_FORM: PetitionCreateFormValues = {
  petitionType: PetitionType.LostMemberCard,
  note: '',
  targetMembershipType: '',
  attachmentFile: null,
};

function petitionTypeLabel(type: PetitionType, t: (k: string) => string): string {
  if (type === PetitionType.ChangeMembershipType) {
    return t('petitions.types.changeMembershipType');
  }
  return t('petitions.types.lostMemberCard');
}

function statusLabel(status: MemberPetitionStatus, t: (k: string) => string): string {
  switch (status) {
    case MemberPetitionStatus.Approved:
      return t('petitions.statusApproved');
    case MemberPetitionStatus.Rejected:
      return t('petitions.statusRejected');
    case MemberPetitionStatus.Pending:
    default:
      return t('petitions.statusPending');
  }
}

function statusTone(status: MemberPetitionStatus): 'pending' | 'approved' | 'rejected' {
  if (status === MemberPetitionStatus.Approved) return 'approved';
  if (status === MemberPetitionStatus.Rejected) return 'rejected';
  return 'pending';
}

function StatusIcon({ tone }: { tone: 'pending' | 'approved' | 'rejected' }) {
  if (tone === 'approved') return <CheckCircleIcon aria-hidden />;
  if (tone === 'rejected') return <XCircleIcon aria-hidden />;
  return <ClockIcon aria-hidden />;
}

function formatDate(dateString: string, language: string): string {
  return formatAthensDate(dateString, language);
}

function formatDateTime(dateString: string, language: string): string {
  return formatAthensDateTime(dateString, language);
}

type StatusFilter = MemberPetitionStatus | 'all';

const Petitions: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { success: showSuccess, error: showError } = useToast();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  const [list, setList] = useState<MemberPetition[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [detailPetition, setDetailPetition] = useState<MemberPetition | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formValues, setFormValues] = useState<PetitionCreateFormValues>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const locale = i18n.language;

  const loadPetitions = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await memberPetitionService.getMyPetitions();
      setList(data);
    } catch {
      const message = t('petitions.loadError');
      setLoadError(message);
      showErrorRef.current(message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const data = await memberPetitionService.getMyPetitions();
        if (!cancelled) {
          setList(data);
        }
      } catch {
        if (!cancelled) {
          const message = t('petitions.loadError');
          setLoadError(message);
          showErrorRef.current(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // Intentionally only when auth changes — avoid toast/i18n churn refetch loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  const statusCounts = useMemo(
    () => ({
      pending: list.filter((p) => p.status === MemberPetitionStatus.Pending).length,
      approved: list.filter((p) => p.status === MemberPetitionStatus.Approved).length,
      rejected: list.filter((p) => p.status === MemberPetitionStatus.Rejected).length,
    }),
    [list]
  );

  const filteredList = useMemo(() => {
    if (statusFilter === 'all') return list;
    return list.filter((p) => p.status === statusFilter);
  }, [list, statusFilter]);

  const openCreateModal = () => {
    setFormValues(EMPTY_FORM);
    setShowCreateModal(true);
  };

  const handleFormChange = (partial: Partial<PetitionCreateFormValues>) => {
    setFormValues((prev) => {
      const next = { ...prev, ...partial };
      if (partial.petitionType !== undefined && partial.petitionType !== prev.petitionType) {
        next.attachmentFile = null;
        next.targetMembershipType = '';
        if (partial.petitionType === PetitionType.LostMemberCard) {
          // keep note; card replacement still needs a reason
        }
      }
      return next;
    });
  };

  const hasPendingCardReplacement = useMemo(
    () =>
      list.some(
        p =>
          normalizePetitionType(p.petitionType) === PetitionType.LostMemberCard &&
          normalizePetitionStatus(p.status) === MemberPetitionStatus.Pending
      ),
    [list]
  );

  const handleCreateSubmit = async () => {
    if (!user?.id) return;
    const trimmedNote = formValues.note.trim();
    if (
      formValues.petitionType === PetitionType.LostMemberCard &&
      hasPendingCardReplacement
    ) {
      showError(t('petitions.pendingReplacementExists', {
        defaultValue: 'A card replacement petition is already pending review.',
      }));
      return;
    }
    if (formValues.petitionType === PetitionType.LostMemberCard && !trimmedNote) {
      showError(
        t('petitions.reasonRequired', {
          defaultValue: 'Please provide a short reason for the card replacement.',
        })
      );
      return;
    }
    if (formValues.petitionType === PetitionType.ChangeMembershipType) {
      if (!formValues.targetMembershipType) {
        showError(
          t('petitions.targetMembershipRequired', {
            defaultValue: 'Please select the membership category you are requesting.',
          })
        );
        return;
      }
      if (!trimmedNote) {
        showError(
          t('petitions.membershipReasonRequired', {
            defaultValue: 'Please explain why you are requesting a membership change.',
          })
        );
        return;
      }
    }
    try {
      setSubmitting(true);
      let attachmentDocumentId: string | undefined;
      if (
        formValues.petitionType === PetitionType.ChangeMembershipType &&
        formValues.attachmentFile
      ) {
        const doc = await documentService.uploadDocument({
          file: formValues.attachmentFile,
          documentType: DocumentType.PetitionAttachment,
          entityType: EntityType.IndividualMember,
          entityId: user.id,
        });
        attachmentDocumentId = doc.id;
      }
      const payload =
        formValues.petitionType === PetitionType.ChangeMembershipType
          ? JSON.stringify({
              targetMembershipType: formValues.targetMembershipType,
              reason: trimmedNote,
            })
          : trimmedNote || undefined;
      const body: CreatePetitionRequest = {
        petitionType: formValues.petitionType,
        payload,
        attachmentDocumentId,
      };
      await memberPetitionService.createPetition(body);
      showSuccess(t('petitions.createSuccess'));
      setShowCreateModal(false);
      setFormValues(EMPTY_FORM);
      await loadPetitions();
    } catch (err) {
      const raw = err instanceof Error ? err.message : '';
      showError(raw ? getApiErrorMessage(raw, t) : t('petitions.createError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadAttachment = async (docId: string, fileName?: string) => {
    try {
      const blob = await documentService.downloadDocument(docId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'attachment';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('petitions.loadError'));
    }
  };

  if (!user) return null;

  return (
    <PetitionsContainer>
      <Stack direction="column" spacing="xl">
        <PageHeader
          title={t('petitions.myPetitions')}
          subtitle={t('petitions.myPetitionsSubtitle')}
          pattern
          actions={
            <Button onClick={openCreateModal} variant="primary">
              {t('petitions.newPetition')}
            </Button>
          }
        />

        <HelpCard variant="elevated" accentBorder="left">
          <Heading variant="h4" color="text">
            {t('petitions.helpTitle')}
          </Heading>
          <Text size="sm" color="secondary" style={{ marginTop: spacing[2], lineHeight: 1.6 }}>
            {t('petitions.helpDescription')}
          </Text>
          <HelpSteps>
            <li>{t('petitions.helpStep1')}</li>
            <li>{t('petitions.helpStep2')}</li>
            <li>{t('petitions.helpStep3')}</li>
          </HelpSteps>
        </HelpCard>

        {loading ? (
          <SkeletonList aria-busy="true" aria-label={t('common.loading')}>
            {[1, 2, 3].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </SkeletonList>
        ) : loadError ? (
          <ErrorWrap role="alert">
            <ExclamationTriangleIcon width={40} height={40} aria-hidden />
            <Text size="lg">{loadError}</Text>
            <Button variant="primary" onClick={loadPetitions}>
              {t('common.retry')}
            </Button>
          </ErrorWrap>
        ) : list.length === 0 ? (
          <EmptyState
            icon="documentText"
            title={t('petitions.emptyTitle')}
            text={t('petitions.emptyDescription')}
          >
            <Button onClick={openCreateModal} variant="primary">
              {t('petitions.newPetition')}
            </Button>
          </EmptyState>
        ) : (
          <Stack direction="column" spacing="lg">
            <SummaryRow role="group" aria-label={t('petitions.statusSummary')}>
              {(
                [
                  {
                    key: MemberPetitionStatus.Pending,
                    count: statusCounts.pending,
                    label: t('petitions.summaryPending'),
                    tone: 'pending' as const,
                  },
                  {
                    key: MemberPetitionStatus.Approved,
                    count: statusCounts.approved,
                    label: t('petitions.summaryApproved'),
                    tone: 'approved' as const,
                  },
                  {
                    key: MemberPetitionStatus.Rejected,
                    count: statusCounts.rejected,
                    label: t('petitions.summaryRejected'),
                    tone: 'rejected' as const,
                  },
                ]
              ).map(({ key, count, label, tone }) => (
                <SummaryPill
                  key={key}
                  type="button"
                  $active={statusFilter === key}
                  $tone={tone}
                  onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
                  aria-pressed={statusFilter === key}
                >
                  <SummaryCount>{count}</SummaryCount>
                  <SummaryLabel>{label}</SummaryLabel>
                </SummaryPill>
              ))}
            </SummaryRow>

            <FiltersBar>
              <Text size="sm" color="secondary">
                {t('petitions.showingCount', { count: filteredList.length, total: list.length })}
              </Text>
              <Flex direction="row" align="center" gap="sm">
                <Label htmlFor="petition-status-filter">{t('petitions.filterByStatus')}</Label>
                <FilterSelect
                  id="petition-status-filter"
                  value={statusFilter === 'all' ? 'all' : String(statusFilter)}
                  onChange={(e) =>
                    setStatusFilter(
                      e.target.value === 'all'
                        ? 'all'
                        : (Number(e.target.value) as MemberPetitionStatus)
                    )
                  }
                >
                  <option value="all">{t('petitions.allStatuses')}</option>
                  <option value={MemberPetitionStatus.Pending}>{t('petitions.statusPending')}</option>
                  <option value={MemberPetitionStatus.Approved}>{t('petitions.statusApproved')}</option>
                  <option value={MemberPetitionStatus.Rejected}>{t('petitions.statusRejected')}</option>
                </FilterSelect>
              </Flex>
            </FiltersBar>

            {filteredList.length === 0 ? (
              <EmptyState
                icon="search"
                title={t('petitions.noFilteredResults')}
                text={t('petitions.noFilteredResultsDescription')}
              >
                <Button variant="outline" onClick={() => setStatusFilter('all')}>
                  {t('petitions.clearFilter')}
                </Button>
              </EmptyState>
            ) : (
              <div role="list" aria-label={t('petitions.myPetitions')}>
                <Stack direction="column" spacing="md">
                  {filteredList.map((p) => {
                    const tone = statusTone(p.status);
                    return (
                      <PetitionCardButton
                        key={p.id}
                        type="button"
                        role="listitem"
                        aria-label={`${petitionTypeLabel(p.petitionType, t)}, ${statusLabel(p.status, t)}`}
                        onClick={() => setDetailPetition(p)}
                      >
                        <PetitionCard variant="elevated">
                          <CardTop>
                            <CardMeta>
                              <Heading variant="h4" color="text">
                                {petitionTypeLabel(p.petitionType, t)}
                              </Heading>
                              {formatPetitionPayload(p.payload, t) ? (
                                <PayloadPreview size="sm" color="secondary">
                                  {formatPetitionPayload(p.payload, t)}
                                </PayloadPreview>
                              ) : (
                                <Text size="sm" color="secondary" style={{ fontStyle: 'italic' }}>
                                  {t('petitions.noReasonProvided')}
                                </Text>
                              )}
                            </CardMeta>
                            <StatusBadge $tone={tone}>
                              <StatusIcon tone={tone} />
                              {statusLabel(p.status, t)}
                            </StatusBadge>
                          </CardTop>
                          <Flex direction="row" justify="space-between" align="center" wrap="wrap" gap="sm">
                            <Text size="sm" color="secondary">
                              {t('petitions.requestedAt')}: {formatDate(p.requestedAt, locale)}
                            </Text>
                            <ViewHint>
                              {t('petitions.viewDetails')}
                              <ChevronRightIcon aria-hidden />
                            </ViewHint>
                          </Flex>
                        </PetitionCard>
                      </PetitionCardButton>
                    );
                  })}
                </Stack>
              </div>
            )}
          </Stack>
        )}
      </Stack>

      <Modal
        isOpen={!!detailPetition}
        onClose={() => setDetailPetition(null)}
        title={detailPetition ? petitionTypeLabel(detailPetition.petitionType, t) : ''}
        footer={
          <Button onClick={() => setDetailPetition(null)}>
            {t('petitions.closeDetail')}
          </Button>
        }
      >
        {detailPetition && (
          <DetailGrid>
            <DetailRow>
              <DetailLabel>{t('petitions.status')}</DetailLabel>
              <StatusBadge $tone={statusTone(detailPetition.status)}>
                <StatusIcon tone={statusTone(detailPetition.status)} />
                {statusLabel(detailPetition.status, t)}
              </StatusBadge>
            </DetailRow>

            <DetailRow>
              <DetailLabel>{t('petitions.yourReason')}</DetailLabel>
              <DetailText>
                {formatPetitionPayload(detailPetition.payload, t) ||
                  t('petitions.noReasonProvided')}
              </DetailText>
            </DetailRow>

            <DetailRow>
              <DetailLabel>{t('petitions.requestedAt')}</DetailLabel>
              <DetailText>{formatDateTime(detailPetition.requestedAt, locale)}</DetailText>
            </DetailRow>

            {detailPetition.reviewedAt && (
              <DetailRow>
                <DetailLabel>{t('petitions.reviewedAt')}</DetailLabel>
                <DetailText>{formatDateTime(detailPetition.reviewedAt, locale)}</DetailText>
              </DetailRow>
            )}

            {detailPetition.status === MemberPetitionStatus.Rejected &&
              detailPetition.rejectionReason && (
                <RejectionBlock role="alert">
                  <Label>{t('petitions.rejectionReason')}</Label>
                  <Text size="sm" style={{ marginTop: spacing[2] }}>
                    {detailPetition.rejectionReason}
                  </Text>
                </RejectionBlock>
              )}

            {detailPetition.attachmentDocumentId && (
              <DetailRow>
                <DetailLabel>{t('petitions.sectionAttachment')}</DetailLabel>
                <div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      handleDownloadAttachment(
                        detailPetition.attachmentDocumentId!,
                        detailPetition.attachmentFileName
                      )
                    }
                  >
                    {detailPetition.attachmentFileName
                      ? `${t('common.download')}: ${detailPetition.attachmentFileName}`
                      : t('common.download')}
                  </Button>
                </div>
              </DetailRow>
            )}
          </DetailGrid>
        )}
      </Modal>

      <Modal
        isOpen={showCreateModal}
        onClose={() => !submitting && setShowCreateModal(false)}
        title={t('petitions.newPetition')}
        maxWidth="560px"
      >
        <PetitionCreateForm
          values={formValues}
          submitting={submitting}
          hasPendingCardReplacement={hasPendingCardReplacement}
          onChange={handleFormChange}
          onSubmit={handleCreateSubmit}
          onCancel={() => !submitting && setShowCreateModal(false)}
        />
      </Modal>
    </PetitionsContainer>
  );
};

export default Petitions;
